import { FC, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import cn from 'classnames';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from 'react-bootstrap';
import { LandscapeResponseV3Dto } from '../../../store/landscapeAPI/landscape/dto/response/landscape-response-v3.dto';
import { CustomDropdown } from '../../molecules';
import { Action, Button, Label } from '../../atoms';
import RHFForm from '../../atoms/Form/RHFForm';
import Radio from '../../atoms/Radio';
import {
  AddEditLandscapeForm,
  addEditLandscapeSchema,
  buildDefaultValues,
} from '../../../store/landscapeAPI/landscape/schemas/addLandscape.schema';
import { CountryCodeNames } from '../../../store/landscapeAPI/landscape/landscape-country-code-enum';
import { InsertInitiativeDTOV3 } from '../../../store/landscapeAPI/landscape/dto/request/insert-initiative.dto';
import { Locale, LocalesLanguagesConstants } from '../../../locales/locales';
import { UpdateInitiativeDto } from '../../../store/landscapeAPI/landscape/dto/request/update-initiative-dto';
import styles from './LandscapeBasicInfoOrganism.module.css';

interface LandscapeBasicInfoOrganismProps {
  isEdit: boolean;
  landscape?: Map<string, LandscapeResponseV3Dto>;
  callbackFn: (
    landscape: InsertInitiativeDTOV3 | UpdateInitiativeDto,
    languageCode: string,
    translateToAllLanguages: boolean
  ) => void;
  onDeleteClick?: () => void;
}

const LandscapeBasicInfoOrganism: FC<LandscapeBasicInfoOrganismProps> = ({
  isEdit = false,
  landscape: _landscape,
  callbackFn,
  onDeleteClick,
}) => {
  const form = useForm<AddEditLandscapeForm>({
    resolver: zodResolver(addEditLandscapeSchema),
    defaultValues: buildDefaultValues(isEdit, _landscape?.get(Locale.En)),
  });
  const {
    formState: { errors },
    control,
    getValues,
    watch,
    reset,
    handleSubmit,
  } = form;

  const language = watch('language');

  useEffect(() => {
    if (!isEdit) return;
    reset(buildDefaultValues(isEdit, _landscape?.get(language)));
  }, [_landscape, isEdit, language, reset]);

  return (
    <>
      <RHFForm form={form} id="addEditLandscapeForm">
        {isEdit && (
          <Form.Group className={styles.languageWrapper}>
            <Form.Label>Language</Form.Label>
            <Controller
              name="language"
              control={control}
              render={({ field: { onChange } }) => (
                <>
                  <CustomDropdown
                    items={Object.entries(LocalesLanguagesConstants).map(
                      ([key, value]) => ({ label: value, eventKey: key })
                    )}
                    initialTitle={
                      LocalesLanguagesConstants[
                        getValues(
                          'language'
                        ) as keyof typeof LocalesLanguagesConstants
                      ] || 'Select language'
                    }
                    onSelectOption={(e) => {
                      onChange(e.eventKey);
                    }}
                  />
                  {errors?.language && (
                    <p className={styles.errorText}>
                      {errors.language.message}
                    </p>
                  )}
                </>
              )}
            />
          </Form.Group>
        )}
        <div className={cn(styles.landscapeWrapper)}>
          <Form.Group className="mb-3">
            <Form.Label>Name</Form.Label>
            <Controller
              name="name"
              control={control}
              render={({ field: { value, onChange } }) => (
                <>
                  <Form.Control
                    type="text"
                    placeholder="Enter name"
                    onChange={onChange}
                    value={value}
                  />
                  {errors?.name && (
                    <p className={styles.errorText}>{errors.name.message}</p>
                  )}
                </>
              )}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Landscape type</Form.Label>
            <Controller
              name="landscapeType"
              control={control}
              render={({ field: { value, onChange } }) => (
                <>
                  <Form.Control
                    type="text"
                    placeholder="Enter Landscape type"
                    onChange={onChange}
                    value={value}
                  />
                  {errors?.landscapeType && (
                    <p className={styles.errorText}>
                      {errors.landscapeType.message}
                    </p>
                  )}
                </>
              )}
            />
          </Form.Group>
          <hr />
          <Label content="Location" size="heading-l" />
          <Form.Group className="mb-3">
            <Form.Label>Jurisdiction Name</Form.Label>
            <Controller
              name="jurisdictionName"
              control={control}
              render={({ field: { value, onChange } }) => (
                <>
                  <Form.Control
                    type="text"
                    placeholder="Enter Jurisdiction name"
                    value={value}
                    onChange={onChange}
                  />
                  {errors?.jurisdictionName && (
                    <p className={styles.errorText}>
                      {errors.jurisdictionName.message}
                    </p>
                  )}
                </>
              )}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Country</Form.Label>
            <Controller
              name="countryCode"
              control={control}
              render={({ field: { value, onChange } }) => (
                <>
                  <CustomDropdown
                    items={Object.entries(CountryCodeNames).map(
                      ([key, value]) => ({ label: value, eventKey: key })
                    )}
                    initialTitle={CountryCodeNames[value] || 'Select country'}
                    onSelectOption={(e) => {
                      onChange(e.eventKey);
                    }}
                  />
                  {errors?.countryCode && (
                    <p className={styles.errorText}>
                      {errors.countryCode.message}
                    </p>
                  )}
                </>
              )}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Latitude</Form.Label>
            <Controller
              name="latitude"
              control={control}
              render={({ field: { value, onChange } }) => (
                <>
                  <Form.Control
                    type="number"
                    placeholder="Enter latitude"
                    value={Number(value)}
                    onChange={(e) => {
                      let amount = e.target.value;
                      amount = amount
                        .replace(/[^0-9.-]/g, '')
                        .replace(/\.{2,}/g, '.');
                      if (parseFloat(amount) < -90) {
                        return -90;
                      } else if (parseFloat(amount) > 90) {
                        return 90;
                      }
                      onChange(e);
                    }}
                  />
                  {errors?.latitude && (
                    <p className={styles.errorText}>
                      {errors.latitude.message}
                    </p>
                  )}
                </>
              )}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Longitude</Form.Label>
            <Controller
              name="longitude"
              control={control}
              render={({ field: { value, onChange } }) => (
                <>
                  <Form.Control
                    type="number"
                    placeholder="Enter longitude"
                    value={Number(value)}
                    onChange={(e) => {
                      let amount = e.target.value;
                      amount = amount
                        .replace(/[^0-9.-]/g, '')
                        .replace(/\.{3,}/g, '.');
                      if (parseFloat(amount) < -180) {
                        return -180;
                      } else if (parseFloat(amount) > 180) {
                        return 180;
                      }
                      onChange(e);
                    }}
                  />
                  {errors?.longitude && (
                    <p className={styles.errorText}>
                      {errors.longitude.message}
                    </p>
                  )}
                </>
              )}
            />
          </Form.Group>
          <hr />
          <Label content="Contact Person" size="heading-l" />
          <Form.Group className="mb-3">
            <Form.Label>Name</Form.Label>
            <Controller
              name="contactPersonName"
              control={control}
              render={({ field: { value, onChange } }) => (
                <>
                  <Form.Control
                    type="text"
                    placeholder="Enter contact person name"
                    onChange={onChange}
                    value={value}
                    disabled={isEdit}
                  />
                  {errors?.contactPersonName && (
                    <p className={styles.errorText}>
                      {errors.contactPersonName.message}
                    </p>
                  )}
                </>
              )}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Role</Form.Label>
            <Controller
              name="contactPersonRole"
              control={control}
              render={({ field: { value, onChange } }) => (
                <>
                  <Form.Control
                    type="text"
                    placeholder="Enter contact person role"
                    onChange={onChange}
                    value={value}
                    disabled={isEdit}
                  />
                  {errors?.contactPersonRole && (
                    <p className={styles.errorText}>
                      {errors.contactPersonRole.message}
                    </p>
                  )}
                </>
              )}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>E-mail</Form.Label>
            <Controller
              name="contactPersonEmail"
              control={control}
              render={({ field: { value, onChange } }) => (
                <>
                  <Form.Control
                    type="text"
                    placeholder="Enter contact person e-mail"
                    onChange={onChange}
                    value={value}
                    disabled={isEdit}
                  />
                  {errors?.contactPersonEmail && (
                    <p className={styles.errorText}>
                      {errors.contactPersonEmail.message}
                    </p>
                  )}
                </>
              )}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Is Landscape initiative</Form.Label>
            <Controller
              name="isLandscapeInitiative"
              control={control}
              render={({ field: { value, onChange } }) => (
                <>
                  <Radio defaultChecked={value} onChange={onChange} />
                  {errors?.isLandscapeInitiative && (
                    <p className={styles.errorText}>
                      {errors.isLandscapeInitiative.message}
                    </p>
                  )}
                </>
              )}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Is Published</Form.Label>
            <Controller
              name="isPublished"
              control={control}
              render={({ field: { value, onChange } }) => (
                <>
                  <Radio defaultChecked={value} onChange={onChange} />
                  {errors?.isPublished && (
                    <p className={styles.errorText}>
                      {errors.isPublished.message}
                    </p>
                  )}
                </>
              )}
            />
          </Form.Group>
          <div className={styles.submitButtonsWrapper}>
            {isEdit && (
              <Button
                type="submit"
                variant="light"
                form="addEditLandscapeForm"
                onClick={handleSubmit((data) =>
                  callbackFn(
                    data,
                    isEdit ? getValues('language') : Locale.En,
                    true
                  )
                )}
              >
                Translate to all languages
              </Button>
            )}
            <Button
              type="submit"
              variant="dark"
              form="addEditLandscapeForm"
              onClick={handleSubmit((data) =>
                callbackFn(
                  data,
                  isEdit ? getValues('language') : Locale.En,
                  false
                )
              )}
            >
              Save
            </Button>
          </div>
        </div>
      </RHFForm>
      {isEdit && (
        <div className={cn(styles.deleteActionWrapper)}>
          <Action
            type="delete"
            itemName={_landscape?.get(Locale.En)?.name}
            onClick={onDeleteClick}
          />
        </div>
      )}
    </>
  );
};

export default LandscapeBasicInfoOrganism;
